import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';

import { useRequestUnavailableDocumentTypes } from 'services/repositories/relationships/relationships';
import { addNotification } from 'stores/notifications/notifications';
import { BasePopup } from 'components/Display/BasePopup/BasePopup';
import { Button } from 'components/Form/Button/Button';
import { Form } from 'components/Form/Form';
import { Textarea } from 'components/Form/Input/Textarea/Textarea';
import { ValidationErrors } from 'components/Form/ValidationErrors/ValidationErrors';

type DocumentTypeRequestModalProps = {
  open: boolean;
  onClose: () => void;
  requestedPartnerId?: string;
};

type DocumentTypeRequestForm = {
  documentTypes: string;
};

const defaultData: DocumentTypeRequestForm = {
  documentTypes: '',
};

export function DocumentTypeRequestModal({ open, onClose, requestedPartnerId }: DocumentTypeRequestModalProps) {
  const { t } = useTranslation();
  const [nonInputErrors, setNonInputErrors] = useState<Array<string>>([]);

  const formMethods = useForm<DocumentTypeRequestForm>({
    defaultValues: defaultData,
    resolver: yupResolver(
      Yup.object().shape({
        documentTypes: Yup.string().required(
          t('partners:request.documentTypeRequest.validation.documentTypesRequired'),
        ),
      }),
    ),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = formMethods;
  const { mutate: requestDocumentTypes, isLoading } = useRequestUnavailableDocumentTypes();

  const onSubmit = (data: DocumentTypeRequestForm) => {
    setNonInputErrors([]);

    requestDocumentTypes(
      { message: data.documentTypes, requested_partner_id: requestedPartnerId },
      {
        onSuccess: () => {
          addNotification(t('partners:request.documentTypeRequest.modal.success'));
          handleClose();
        },
        onError: (error) => {
          setNonInputErrors([error.message || t('partners:request.documentTypeRequest.modal.error')]);
        },
      },
    );
  };

  const handleClose = () => {
    reset();
    setNonInputErrors([]);
    onClose();
  };

  return (
    <BasePopup open={open} setOpen={handleClose} width="md">
      <BasePopup.Header title={t('partners:request.documentTypeRequest.modal.title')} />
      <BasePopup.Body>
        <ValidationErrors errors={nonInputErrors} />

        <Form id="documentTypeRequestForm" onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="space-y-3">
            <div className="space-y-2.5">
              <div className="space-y-1.5">
                <div className="text-sm font-medium text-gray-900">
                  {t('partners:request.documentTypeRequest.modal.fieldLabel')}
                </div>
              </div>
              <Textarea
                {...register('documentTypes')}
                placeholder=""
                rows={3}
                className="w-full"
                errors={errors.documentTypes}
              />
            </div>

            <div className="rounded-md bg-gray-50 p-4">
              <div className="text-sm leading-5 font-normal text-gray-900">
                <p>{t('partners:request.documentTypeRequest.modal.supportTeamMessage')}</p>
                <p>{t('partners:request.documentTypeRequest.modal.availabilityNote')}</p>
              </div>
            </div>
          </div>
        </Form>

        <div className="mt-6 flex items-center justify-end gap-3">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading} analyticsId="cancel_button">
            {t('partners:request.documentTypeRequest.modal.cancelButton')}
          </Button>
          <Button type="submit" form="documentTypeRequestForm" loading={isLoading} analyticsId="submit_button">
            {t('partners:request.documentTypeRequest.modal.submitButton')}
          </Button>
        </div>
      </BasePopup.Body>
    </BasePopup>
  );
}
